import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Mail, MessageSquare, Webhook, Zap, Database, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const INTEGRATION_TYPES = [
  {
    type: 'email',
    name: 'Email Notifications',
    icon: Mail,
    description: 'Send email notifications on form submission',
    color: 'bg-blue-500'
  },
  {
    type: 'whatsapp',
    name: 'WhatsApp Messages',
    icon: MessageSquare,
    description: 'Send WhatsApp notifications via Interakt',
    color: 'bg-green-500'
  },
  {
    type: 'google_sheets',
    name: 'Google Sheets',
    icon: FileSpreadsheet,
    description: 'Sync submissions to Google Sheets',
    color: 'bg-green-600'
  },
  {
    type: 'webhook',
    name: 'Webhook',
    icon: Webhook,
    description: 'Send data to custom webhook URL',
    color: 'bg-purple-500'
  },
  {
    type: 'zapier',
    name: 'Zapier',
    icon: Zap,
    description: 'Connect with 5000+ apps via Zapier',
    color: 'bg-orange-500'
  },
  {
    type: 'database',
    name: 'Database',
    icon: Database,
    description: 'Export to external database',
    color: 'bg-slate-600'
  }
];

const Integrations = ({ integrations, onUpdate }) => {
  const [activeIntegrations, setActiveIntegrations] = useState(integrations || []);

  const toggleIntegration = (type) => {
    const exists = activeIntegrations.find(i => i.type === type);
    
    if (exists) {
      const updated = activeIntegrations.filter(i => i.type !== type);
      setActiveIntegrations(updated);
      onUpdate(updated);
      toast.success('Integration disabled');
    } else {
      const newIntegration = {
        type,
        enabled: true,
        config: {},
        trigger: 'on_submit'
      };
      const updated = [...activeIntegrations, newIntegration];
      setActiveIntegrations(updated);
      onUpdate(updated);
      toast.success('Integration enabled');
    }
  };

  const updateIntegrationConfig = (type, config) => {
    const updated = activeIntegrations.map(i => 
      i.type === type ? { ...i, config: { ...i.config, ...config } } : i
    );
    setActiveIntegrations(updated);
    onUpdate(updated);
  };

  const isEnabled = (type) => {
    return activeIntegrations.some(i => i.type === type && i.enabled);
  };

  const getConfig = (type) => {
    return activeIntegrations.find(i => i.type === type)?.config || {};
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-outfit font-semibold text-slate-900 mb-2">Integrations</h3>
        <p className="text-sm text-slate-600 font-jakarta">
          Connect your form with external services to automate workflows
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {INTEGRATION_TYPES.map((integration) => {
          const Icon = integration.icon;
          const enabled = isEnabled(integration.type);
          const config = getConfig(integration.type);

          return (
            <Card 
              key={integration.type}
              className={`border transition-all duration-200 ${
                enabled 
                  ? 'border-primary bg-primary-subtle' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className={`w-10 h-10 rounded-lg ${integration.color} flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-outfit font-semibold text-slate-900">{integration.name}</h4>
                      <p className="text-xs text-slate-600 font-jakarta mt-1">
                        {integration.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={() => toggleIntegration(integration.type)}
                  />
                </div>

                {enabled && (
                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                    {integration.type === 'email' && (
                      <>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Recipients</Label>
                          <Input
                            value={config.recipients || ''}
                            onChange={(e) => updateIntegrationConfig('email', { recipients: e.target.value })}
                            placeholder="email@example.com, email2@example.com"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Subject</Label>
                          <Input
                            value={config.subject || ''}
                            onChange={(e) => updateIntegrationConfig('email', { subject: e.target.value })}
                            placeholder="New form submission"
                            className="text-sm"
                          />
                        </div>
                      </>
                    )}

                    {integration.type === 'whatsapp' && (
                      <>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Interakt API Key</Label>
                          <Input
                            type="password"
                            value={config.api_key || ''}
                            onChange={(e) => updateIntegrationConfig('whatsapp', { api_key: e.target.value })}
                            placeholder="Enter your Interakt API key"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Template ID</Label>
                          <Input
                            value={config.template_id || ''}
                            onChange={(e) => updateIntegrationConfig('whatsapp', { template_id: e.target.value })}
                            placeholder="Template ID"
                            className="text-sm"
                          />
                        </div>
                      </>
                    )}

                    {integration.type === 'google_sheets' && (
                      <>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Spreadsheet ID</Label>
                          <Input
                            value={config.spreadsheet_id || ''}
                            onChange={(e) => updateIntegrationConfig('google_sheets', { spreadsheet_id: e.target.value })}
                            placeholder="Google Sheets ID"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Sheet Name</Label>
                          <Input
                            value={config.sheet_name || ''}
                            onChange={(e) => updateIntegrationConfig('google_sheets', { sheet_name: e.target.value })}
                            placeholder="Sheet1"
                            className="text-sm"
                          />
                        </div>
                      </>
                    )}

                    {integration.type === 'webhook' && (
                      <>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Webhook URL</Label>
                          <Input
                            value={config.url || ''}
                            onChange={(e) => updateIntegrationConfig('webhook', { url: e.target.value })}
                            placeholder="https://your-webhook-url.com"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Method</Label>
                          <select
                            value={config.method || 'POST'}
                            onChange={(e) => updateIntegrationConfig('webhook', { method: e.target.value })}
                            className="w-full h-9 rounded-lg border border-slate-200 px-2 text-sm"
                          >
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                            <option value="PATCH">PATCH</option>
                          </select>
                        </div>
                      </>
                    )}

                    {integration.type === 'zapier' && (
                      <div>
                        <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Zapier Webhook URL</Label>
                        <Input
                          value={config.webhook_url || ''}
                          onChange={(e) => updateIntegrationConfig('zapier', { webhook_url: e.target.value })}
                          placeholder="https://hooks.zapier.com/..."
                          className="text-sm"
                        />
                      </div>
                    )}

                    {integration.type === 'database' && (
                      <>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Database Type</Label>
                          <select
                            value={config.db_type || 'postgresql'}
                            onChange={(e) => updateIntegrationConfig('database', { db_type: e.target.value })}
                            className="w-full h-9 rounded-lg border border-slate-200 px-2 text-sm"
                          >
                            <option value="postgresql">PostgreSQL</option>
                            <option value="mysql">MySQL</option>
                            <option value="mongodb">MongoDB</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-700 font-jakarta mb-1 block">Connection String</Label>
                          <Input
                            type="password"
                            value={config.connection_string || ''}
                            onChange={(e) => updateIntegrationConfig('database', { connection_string: e.target.value })}
                            placeholder="Database connection string"
                            className="text-sm"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Integrations;
